import { useEffect, useRef, useState } from "react";
import { APIProvider, Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
export const hasGoogleKey = !!KEY;

const DARK = [
  { elementType: "geometry", stylers: [{ color: "#1d2433" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9aa4b5" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#11161f" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#2d3648" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3e4a63" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0b1220" }] },
  { featureType: "poi", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
];

function LiveDirections({ origin, dest, onInfo }) {
  const map = useMap();
  const routes = useMapsLibrary("routes");
  const renderer = useRef(null);
  const marker = useRef(null);
  const oKey = origin ? `${origin.lat.toFixed(3)},${origin.lng.toFixed(3)}` : null;

  useEffect(() => {
    if (!routes || !map) return undefined;
    renderer.current = new routes.DirectionsRenderer({
      map, preserveViewport: false,
      polylineOptions: { strokeColor: "#D4AF37", strokeWeight: 6 },
    });
    return () => renderer.current?.setMap(null);
  }, [routes, map]);

  useEffect(() => {
    if (!routes || !renderer.current || !oKey) return;
    const [lat, lng] = oKey.split(",").map(Number);
    new routes.DirectionsService()
      .route({ origin: { lat, lng }, destination: dest, travelMode: "DRIVING" })
      .then((r) => {
        renderer.current.setDirections(r);
        const leg = r.routes?.[0]?.legs?.[0];
        if (leg) onInfo({ distance: leg.distance?.text, duration: leg.duration?.text });
      })
      .catch(() => onInfo({ error: "Itinéraire Google indisponible" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes, oKey, dest.lat, dest.lng]);

  useEffect(() => {
    if (!map || !origin || !window.google) return;
    if (!marker.current) {
      marker.current = new window.google.maps.Marker({
        map, zIndex: 999,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: "#003594", fillOpacity: 1, strokeColor: "#D4AF37", strokeWeight: 3 },
      });
    }
    marker.current.setPosition(origin);
  }, [map, origin]);

  useEffect(() => () => marker.current?.setMap(null), []);
  return null;
}

function KeylessEmbed({ origin, dest }) {
  const o = origin ? `${origin.lat.toFixed(3)},${origin.lng.toFixed(3)}` : "";
  const src = `https://maps.google.com/maps?saddr=${o}&daddr=${dest.lat},${dest.lng}&output=embed&hl=fr`;
  return <iframe key={src} title="Itinéraire Google Maps" src={src} className="w-full h-full border-0" loading="lazy" data-testid="google-embed-map" />;
}

export default function GoogleRouteMap({ origin, dest }) {
  const [info, setInfo] = useState(null);
  if (!KEY) return <KeylessEmbed origin={origin} dest={dest} />;
  return (
    <div className="relative w-full h-full" data-testid="google-live-map">
      <APIProvider apiKey={KEY} language="fr" region="SN">
        <Map defaultCenter={origin || dest} defaultZoom={14} styles={DARK} gestureHandling="greedy"
          disableDefaultUI zoomControl style={{ width: "100%", height: "100%" }}>
          <LiveDirections origin={origin} dest={dest} onInfo={setInfo} />
        </Map>
      </APIProvider>
      {info && (
        <div className="absolute bottom-2 inset-x-2 bg-slate-950/85 rounded-xl px-3 py-2 text-[11px] font-black uppercase tracking-widest text-white flex justify-between pointer-events-none" data-testid="google-route-info">
          <span className="text-[#D4AF37]">→ Destination</span>
          <span>{info.error || `${info.distance} · ${info.duration}`}</span>
        </div>
      )}
    </div>
  );
}
